'use client';

import { useMemo } from 'react';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { getCurrencySymbolFromEnum } from 'lib/region/countryUtils';
import { formatPriceWithComma } from 'hooks/pricing/useFormattedPrice';
import { Currency } from 'utils/Currency';
import { ClueShopifyMap } from 'content/products/addons/clue';
import {
  RingPriceStriked,
  RingType,
} from 'content/products/ring/productMap/constants';

export const useCluePricing = () => {
  const { store: regionStore, currency: regionCurrency } = useRegionLocale();

  // Use store/currency from RegionLocaleContext (single source of truth)
  const store = regionStore ?? ShopifyStore.ROW;
  const currency = regionCurrency ?? Currency.usd;

  const currencySymbol = useMemo(
    () => getCurrencySymbolFromEnum(currency),
    [currency],
  );

  const clue = ClueShopifyMap[store] || ClueShopifyMap[ShopifyStore.ROW];
  const cluePrice = clue?.Year1?.price ?? 0;
  const clueDiscounted = clue?.Year1?.discountedPrice ?? 0;

  const ringBase =
    RingPriceStriked[RingType.NOT_ENGRAVED]?.[store] ??
    RingPriceStriked[RingType.NOT_ENGRAVED]?.[ShopifyStore.ROW] ??
    349;

  const bundlePrice = ringBase + clueDiscounted;

  const format = (input: number) => formatPriceWithComma(input, currency);

  const formatWithSymbol = (input: number) =>
    currency === Currency.aed
      ? `${currencySymbol} ${format(input)}`
      : `${currencySymbol}${format(input)}`;

  return {
    store,
    currency,
    currencySymbol,
    ringBase,
    cluePrice,
    clueDiscounted,
    bundlePrice,
    format,
    formatWithSymbol,
    ringPriceDisplay: formatWithSymbol(ringBase),
    clueDiscountedDisplay: formatWithSymbol(clueDiscounted),
    cluePriceDisplay: formatWithSymbol(cluePrice),
    bundlePriceDisplay: formatWithSymbol(bundlePrice),
  } as const;
};

export default useCluePricing;
